import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Users, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1543372654-b45dd90eeee5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHx1cyUyMGNhcGl0b2wlMjBidWlsZGluZyUyMHdhc2hpbmd0b24lMjBkY3xlbnwwfHx8fDE3ODI2NjMyODF8MA&ixlib=rb-4.1.0&q=85";
const COMMUNITY = "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIweW91bmclMjBwcm9mZXNzaW9uYWxzJTIwb2ZmaWNlfGVufDB8fHx8MTc4MjY2MzI4MXww&ixlib=rb-4.1.0&q=85";
const MEETING = "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIweW91bmclMjBwcm9mZXNzaW9uYWxzJTIwb2ZmaWNlfGVufDB8fHx8MTc4MjY2MzI4MXww&ixlib=rb-4.1.0&q=85";
const PHONE = "https://images.unsplash.com/photo-1707836916010-3c4ad261936c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxhbHVtbmklMjBuZXR3b3JraW5nJTIwYXBwJTIwbW9ja3VwfGVufDB8fHx8MTc4MjY2MzI5MXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="U.S. Capitol" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/95 via-[#0A192F]/75 to-[#0A192F]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 text-[#FDFBF7]">
            <div className="label-eyebrow text-[#C5A059]">Est. 1977 · Executive Order 12008</div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              A foundation of <em className="text-[#C5A059] not-italic">public service</em>,<br/>
              a community for life.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-200 leading-relaxed">
              The Presidential Management Fellows Foundation & Association preserves the legacy of America's premier leadership-development program — and connects the Fellows who have shaped it for nearly five decades.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta-join">
                <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#B38F48] rounded-md h-12 px-8 text-sm font-semibold tracking-wide">
                  Become a member <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/history" data-testid="hero-cta-history">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-md h-12 px-8 text-sm bg-transparent">
                  Read our history
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="grid grid-cols-2 gap-4 text-[#FDFBF7]">
              {[["48+","Years of service"],["12,000+","Fellows trained"],["75+","Agencies"],["50","States & territories"]].map(([v,l]) => (
                <div key={l} className="border border-white/15 bg-white/5 backdrop-blur-sm p-5">
                  <div className="font-display text-4xl text-[#C5A059]">{v}</div>
                  <div className="label-eyebrow text-slate-300 mt-2">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="label-eyebrow">Our charter</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05] text-[#0A192F]">Preserving the legacy. Investing in the next class.</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-slate-700 leading-relaxed">
            <p>Since the program's founding by President Carter in 1977, more than twelve thousand Fellows have entered federal service through the PMF Program. The Foundation & Association exists to <span className="text-[#0A192F] font-medium">preserve that history</span>, <span className="text-[#0A192F] font-medium">connect the community</span>, and <span className="text-[#0A192F] font-medium">advance the cause of public service</span> in every administration.</p>
            <p>This site is the Association's private home — a place for current and former Fellows to share their work, mentor one another, and continue the unfinished business of building a more effective government.</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="divider-gold" /></div>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-3 gap-8" data-testid="pillars">
        {[
          { icon: ShieldCheck, title: "Members-only network", body: "A private feed, groups, and direct messaging — verified to the PMF community.", to: "/register", cta: "Join the Association" },
          { icon: Users, title: "Cohort connections", body: "Discover Fellows by cohort year, agency, and interest. Mentor and be mentored.", to: "/feed", cta: "Open the feed" },
          { icon: Sparkles, title: "Foundation merchandise", body: "Apparel, accessories, and keepsakes that support the Foundation's programs.", to: "/store", cta: "Visit the store" },
        ].map(({ icon: Icon, title, body, to, cta }) => (
          <div key={title} className="card-flat p-8 group hover:-translate-y-1 hover:shadow-lg transition-all">
            <div className="w-10 h-10 rounded-md bg-[#002855] text-[#C5A059] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
            <h3 className="mt-6 font-display text-2xl text-[#0A192F]">{title}</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">{body}</p>
            <Link to={to} className="mt-6 inline-flex items-center text-sm font-medium text-[#002855] group-hover:text-[#C5A059]">
              {cta} <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        ))}
      </section>

      {/* COMMUNITY SPLIT */}
      <section className="bg-[#0A192F] text-[#FDFBF7] relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="label-eyebrow text-[#C5A059]">A private community</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05]">Built for Fellows, by Fellows.</h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-xl">
              Share a win, ask a hard question, or find the colleague whose work changed yours. The Association's feed is a quiet, considered space — closed to outsiders, open to every PMF.
            </p>
            <ul className="mt-8 space-y-3 text-slate-200 text-sm">
              <li className="flex gap-3"><span className="text-[#C5A059]">✦</span> Verified PMF membership</li>
              <li className="flex gap-3"><span className="text-[#C5A059]">✦</span> Cohort & agency discovery</li>
              <li className="flex gap-3"><span className="text-[#C5A059]">✦</span> Direct messaging and groups</li>
              <li className="flex gap-3"><span className="text-[#C5A059]">✦</span> Mentorship matching</li>
            </ul>
            <Link to="/register" className="mt-10 inline-block" data-testid="community-cta">
              <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#B38F48] rounded-md h-12 px-8 font-semibold">Create your member account</Button>
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <img src={COMMUNITY} alt="Fellows collaborating" className="w-full h-72 object-cover" />
            <img src={PHONE} alt="Mobile feed" className="w-full h-72 object-cover translate-y-8" />
            <img src={MEETING} alt="Board meeting" className="col-span-2 w-full h-56 object-cover" />
          </div>
        </div>
      </section>

      {/* STORE TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="label-eyebrow">The Foundation Shop</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-[#0A192F]">Wear the legacy.</h2>
            <p className="mt-3 text-slate-600 max-w-xl">Proceeds from the Foundation Shop fund mentorship and programs across the Association.</p>
          </div>
          <Link to="/store" data-testid="store-cta">
            <Button variant="outline" className="border-[#002855] text-[#002855] hover:bg-[#002855] hover:text-white rounded-md h-11 px-6">Shop the collection</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
